// src/services/EventService.js - FULLY UPDATED VERSION with hierarchical categories
import axios from 'axios';
import moment from 'moment-timezone';

// Use full API URL like your EventDetail component
const apiUrl = process.env.REACT_APP_API_URL ;

class EventService {
  /**
   * Get auth headers for API requests
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Object} - Headers object with authentication
   */
  static getHeaders(getAuthHeaders) {
    return typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
  }

  // ========== EXISTING EVENT METHODS (Updated with hierarchical categories) ==========

  /**
   * Get all events
   * @returns {Promise<Array>} - Array of events
   */
  static async getEvents() {
    try {
      const response = await axios.get(`${apiUrl}/events`);
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Get event by ID
   * @param {number} id - Event ID
   * @returns {Promise<Object>} - Event data
   */
  static async getEventById(id) {
    try {
      const response = await axios.get(`${apiUrl}/events/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event by ID:', error);
      throw error;
    }
  }

  /**
   * Fetch all pending events
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Array>} - Array of pending events
   */
  static async fetchPendingEvents(getAuthHeaders) {
    console.log('Fetching pending events from:', `${apiUrl}/events/pending`);
    
    const response = await axios.get(
      `${apiUrl}/events/pending`,
      this.getHeaders(getAuthHeaders)
    );
    
    return response.data;
  }

  /**
   * Update an existing event with optional status-only mode
   * 🔧 UPDATED: Enhanced with hierarchical categories and status-only support
   * @param {Object} event - The event to update
   * @param {string} status - The new status (approved, rejected, pending)
   * @param {string} comments - Comments for the event update
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @param {Object} options - Update options
   * @param {boolean} options.createGroupForApproved - Whether to create groups for approved events (default: true)
   * @param {boolean} options.statusOnly - Whether to send only status fields (default: false)
   * @returns {Promise<Object>} - The updated event
   */
  static async updateEvent(event, status, comments, getAuthHeaders, options = {}) {
    const { createGroupForApproved = true, statusOnly = false } = options;
    try {
      // For approved events, optionally create groups
      const shouldCreateGroup = (status === 'approved' && createGroupForApproved);

      // 🔧 IMPORTANT: Log original creator information for tracking
      console.log('🔍 [EventService.updateEvent] Request details:', {
        eventId: event.id,
        status,
        statusOnly,
        shouldCreateGroup,
        originalStatus: event.status,
        eventCreatorId: event.event_creator_id,
        venueOwnerId: event.venue_owner_id,
        categories: {
          main: event.main_category,
          sub: event.sub_category,
          type: event.type
        }
      });

      let eventData;

      if (statusOnly) {
        // 🔧 STATUS-ONLY MODE: Send minimal payload
        console.log('📦 [EventService] Using status-only mode - minimal payload');

        eventData = {
          status,
          comments: comments || ''
        };

        // Only add createEventGroup for approved status
        if (status === 'approved') {
          eventData.createEventGroup = shouldCreateGroup;
        }

        // Prune any undefined/empty fields
        Object.keys(eventData).forEach(key => {
          if (eventData[key] === undefined || eventData[key] === null || eventData[key] === '') {
            if (key !== 'comments') { // Keep comments even if empty
              delete eventData[key];
            }
          }
        });

      } else {
        // 🔧 FULL MODE: Send complete payload (backward compatibility)
        console.log('📦 [EventService] Using full mode - complete payload');

        // Format dates
        const eventDate = moment(event.event_date).utc().format();
        const eventEndDate = event.event_end_date
          ? moment(event.event_end_date).utc().format()
          : null;

        eventData = {
          id: event.id,
          eventName: event.event_name,
          eventDate,
          eventEndDate,
          eventLocation: event.event_location,
          eventDescription: event.event_description || '',
          eventImageLink: event.event_image_link || '',
          additionalImageLinks: event.additional_image_links || [],
          latitude: event.latitude,
          longitude: event.longitude,
          websiteLink: event.website_link || '',
          eventSummary: event.event_summary || '',
          language: event.language,

          // 🔧 HIERARCHICAL CATEGORIES: Only include if present
          ...(event.main_category !== undefined && { main_category: event.main_category }),
          ...(event.sub_category !== undefined && { sub_category: event.sub_category }),

          // 🔧 LEGACY: Only include type if present
          ...(event.type !== undefined && { type: event.type }),

          status,
          comments,
          userName: event.user_name,

          // 🔧 CREATOR PRESERVATION: Include creator fields to preserve them
          eventCreatorId: event.event_creator_id,
          venueOwnerId: event.venue_owner_id,

          // Group creation flag
          createEventGroup: shouldCreateGroup
        };
      }

      // Log the payload for debugging
      console.log(`📡 [EventService] Making PUT request to ${apiUrl}/events/${event.id}`);
      console.log('📋 [EventService] Payload details:', {
        statusOnly,
        payloadSize: Object.keys(eventData).length,
        status: eventData.status,
        hasCategories: !!(eventData.main_category || eventData.sub_category || eventData.type),
        createEventGroup: eventData.createEventGroup,
        payload: statusOnly ? eventData : {
          status: eventData.status,
          main_category: eventData.main_category,
          sub_category: eventData.sub_category,
          type: eventData.type,
          createEventGroup: eventData.createEventGroup
        }
      });
      
      // Send the request to update the event
      const response = await axios.put(
        `${apiUrl}/events/${event.id}`, 
        eventData,
        this.getHeaders(getAuthHeaders)
      );
      
      // 🔧 ENHANCED: Verify creator information and categories were preserved
      const updatedEvent = response.data;
      console.log(`Event ${event.id} updated successfully:`, {
        status: response.status,
        updatedEvent: updatedEvent?.id,
        creatorPreserved: updatedEvent?.event_creator_id === event.event_creator_id,
        venueOwnerPreserved: updatedEvent?.venue_owner_id === event.venue_owner_id,
        categories: {
          main: updatedEvent?.main_category,
          sub: updatedEvent?.sub_category,
          legacy: updatedEvent?.type
        }
      });
      
      // 🔧 NEW: Validation warnings
      if (updatedEvent?.event_creator_id !== event.event_creator_id) {
        console.warn('⚠️ Creator ID changed during update:', {
          original: event.event_creator_id,
          updated: updatedEvent?.event_creator_id,
          eventId: event.id
        });
      }
      
      return updatedEvent;
    } catch (error) {
      console.error('Error updating event:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Save a translated event with hierarchical categories
   * 🔧 UPDATED: Enhanced with hierarchical categories
   * @param {Object} event - The translated event to save
   * @param {string} status - The status for the new event (approved, pending)
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @param {boolean} createGroup - Whether to create a group chat for approved events
   * @returns {Promise<Object>} - The saved event
   */
  static async saveTranslatedEvent(event, status, getAuthHeaders, createGroup = true) {
    try {
      // For approved events, always create groups automatically
      const shouldCreateGroup = status === 'approved' ? true : false;
      console.log('EventService.saveTranslatedEvent - Auto-creating groups for approved events:', shouldCreateGroup);
      
      // 🔧 CRITICAL: Validate that creator information exists
      console.log('🔍 Translated event creator validation:', {
        eventId: event.id,
        eventCreatorId: event.event_creator_id,
        venueOwnerId: event.venue_owner_id,
        userName: event.user_name,
        masterEventId: event.master_event_id,
        language: event.language,
        mainCategory: event.main_category,
        subCategory: event.sub_category,
        legacyType: event.type
      });
      
      // Format dates
      const eventDate = moment(event.event_date).utc().format();
      const eventEndDate = event.event_end_date
        ? moment(event.event_end_date).utc().format()
        : null;

      // 🔧 UPDATED: Prepare translated event data with hierarchical categories
      const translatedEventData = {
        eventName: event.event_name,
        eventDate,
        eventEndDate,
        eventLocation: event.event_location,
        eventDescription: event.event_description || '',
        eventImageLink: event.event_image_link || '',
        latitude: event.latitude,
        longitude: event.longitude,
        websiteLink: event.website_link || '',
        eventSummary: event.event_summary || '',
        language: event.language,
        
        // 🔧 NEW: Hierarchical categories
        main_category: event.main_category,
        sub_category: event.sub_category,
        
        // 🔧 LEGACY: Keep type field for backward compatibility
        type: event.type,
        
        status,
        
        // 🔧 CREATOR PRESERVATION: All creator-related fields
        userName: event.user_name || 'other',
        eventCreatorId: event.event_creator_id,
        venueOwnerId: event.venue_owner_id,
        
        // Always create groups for approved events
        createEventGroup: shouldCreateGroup,
        
        // 🔧 MASTER EVENT RELATIONSHIP: Ensure proper master event linking
        masterEventId: event.master_event_id || event.id
      };

      // 🔧 VALIDATION: Check for critical missing data
      if (event.event_creator_id !== null && !translatedEventData.eventCreatorId) {
        console.error('🚨 CRITICAL: Original event has creator but translated version missing creator ID');
        throw new Error('Creator information missing in translated event');
      }

      // Log key parts for debugging
      console.log(`Making POST request to ${apiUrl}/events`);
      console.log('Translated event payload key fields:', {
        eventName: translatedEventData.eventName,
        status: translatedEventData.status,
        language: translatedEventData.language,
        main_category: translatedEventData.main_category,
        sub_category: translatedEventData.sub_category,
        type: translatedEventData.type,
        createEventGroup: translatedEventData.createEventGroup,
        masterEventId: translatedEventData.masterEventId,
        
        // 🔧 ENHANCED: Detailed creator field logging
        originalCreatorId: event.event_creator_id,
        preservedCreatorId: translatedEventData.eventCreatorId,
        originalVenueOwnerId: event.venue_owner_id,
        preservedVenueOwnerId: translatedEventData.venueOwnerId,
        originalUserName: event.user_name,
        preservedUserName: translatedEventData.userName
      });
      
      // Send the request to save the translated event
      const response = await axios.post(
        `${apiUrl}/events`, 
        translatedEventData,
        this.getHeaders(getAuthHeaders)
      );
      
      const newEvent = response.data;
      
      // 🔧 COMPREHENSIVE: Verify all fields were preserved
      console.log('Translated event created successfully:', {
        status: response.status,
        newEventId: newEvent?.id,
        masterEventId: newEvent?.master_event_id,
        
        // Category verification
        categories: {
          main: newEvent?.main_category,
          sub: newEvent?.sub_category,
          legacy: newEvent?.type
        },
        
        // Detailed creator verification
        expectedCreatorId: event.event_creator_id,
        actualCreatorId: newEvent?.event_creator_id,
        creatorPreserved: newEvent?.event_creator_id === event.event_creator_id,
        
        expectedVenueOwnerId: event.venue_owner_id,
        actualVenueOwnerId: newEvent?.venue_owner_id,
        venueOwnerPreserved: newEvent?.venue_owner_id === event.venue_owner_id,
        
        expectedUserName: event.user_name,
        actualUserName: newEvent?.user_name,
        userNamePreserved: newEvent?.user_name === event.user_name
      });
      
      // 🔧 NEW: Validation warnings for any mismatches
      if (newEvent?.event_creator_id !== event.event_creator_id) {
        console.warn('⚠️ Creator ID not preserved in translation:', {
          original: event.event_creator_id,
          translated: newEvent?.event_creator_id,
          originalEventId: event.id,
          translatedEventId: newEvent?.id
        });
      }
      
      if (newEvent?.venue_owner_id !== event.venue_owner_id) {
        console.warn('⚠️ Venue owner ID not preserved in translation:', {
          original: event.venue_owner_id,
          translated: newEvent?.venue_owner_id,
          originalEventId: event.id,
          translatedEventId: newEvent?.id
        });
      }
      
      return newEvent;
    } catch (error) {
      console.error(`Error saving translated event as ${status}:`, error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  // ========== FAVORITES METHODS (Unchanged) ==========

  /**
   * Toggle favorite status for an event
   * @param {number} eventId - The event ID
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - The result of the favorite toggle
   */
  static async toggleFavorite(eventId, getAuthHeaders) {
    try {
      console.log(`Toggling favorite for event ${eventId}`);
      
      const response = await axios.post(
        `${apiUrl}/events/${eventId}/favorite`,
        {}, // Empty body for POST request
        this.getHeaders(getAuthHeaders)
      );
      
      console.log(`Favorite toggled successfully:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Get favorite status for an event
   * @param {number} eventId - The event ID
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - The favorite status
   */
  static async getFavoriteStatus(eventId, getAuthHeaders) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/favorite`,
        this.getHeaders(getAuthHeaders)
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching favorite status:', error);
      // Return default if endpoint doesn't exist yet
      return { isFavorited: false };
    }
  }

  /**
   * Get user's favorite events
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @param {number} limit - Number of events to fetch
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object>} - User's favorite events
   */
  static async getUserFavorites(getAuthHeaders, limit = 50, offset = 0) {
    try {
      const response = await axios.get(
        `${apiUrl}/user/favorites?limit=${limit}&offset=${offset}`,
        this.getHeaders(getAuthHeaders)
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user favorites:', error);
      throw error;
    }
  }

  /**
   * Get combined user status for an event (favorite + participation + groups)
   * @param {number} eventId - The event ID
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - Combined user status
   */
  static async getUserEventStatus(eventId, getAuthHeaders) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/user-status`,
        this.getHeaders(getAuthHeaders)
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user event status:', error);
      throw error;
    }
  }

  /**
   * Get event's favorite count and statistics
   * @param {number} eventId - The event ID
   * @param {boolean} includeUsers - Whether to include list of users who favorited
   * @returns {Promise<Object>} - Event favorite statistics
   */
  static async getEventFavoriteStats(eventId, includeUsers = false) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/favorite-stats?include_users=${includeUsers}`
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching event favorite stats:', error);
      // Return default if endpoint doesn't exist yet
      return { favoriteCount: 0 };
    }
  }

  // ========== PARTICIPATION METHODS (Unchanged) ==========

  /**
   * Update participation status for an event
   * @param {number} eventId - The event ID
   * @param {string} status - The participation status ('interested', 'going', 'not-going')
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - The result of the participation update
   */
  static async updateParticipationStatus(eventId, status, getAuthHeaders) {
    try {
      console.log(`Updating participation for event ${eventId} to status: ${status}`);
      
      const response = await axios.post(
        `${apiUrl}/events/${eventId}/participation`,
        { status },
        this.getHeaders(getAuthHeaders)
      );
      
      console.log(`Participation updated successfully:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating participation status:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Get user's participation status for an event
   * @param {number} eventId - The event ID
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - User's participation status
   */
  static async getParticipationStatus(eventId, getAuthHeaders) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/participation`,
        this.getHeaders(getAuthHeaders)
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching participation status:', error);
      throw error;
    }
  }

  /**
   * Get user's event groups based on participation status
   * @param {number} eventId - The event ID
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - User's accessible event groups
   */
  static async getUserEventGroups(eventId, getAuthHeaders) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/groups`,
        this.getHeaders(getAuthHeaders)
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user event groups:', error);
      throw error;
    }
  }

  /**
   * Get event participation statistics
   * @param {number} eventId - The event ID
   * @returns {Promise<Object>} - Event participation statistics
   */
  static async getEventParticipationStats(eventId) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/stats`
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching event participation stats:', error);
      throw error;
    }
  }

  /**
   * Get all participants for an event
   * @param {number} eventId - The event ID
   * @param {string} status - Optional filter by status ('interested', 'going')
   * @returns {Promise<Array>} - Array of participants
   */
  static async getEventParticipants(eventId, status = null) {
    try {
      let url = `${apiUrl}/events/${eventId}/participants`;
      if (status) {
        url += `?status=${status}`;
      }
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting event participants:', error);
      throw error;
    }
  }

 // ========== CHAT/GROUP METHODS (Unchanged) ==========

/**
 * Get group chat messages for an event
 * @param {number} eventId - The event ID
 * @param {string} chatType - The chat type ('interested' or 'joining')
 * @param {function} getAuthHeaders - Function to get authentication headers
 * @param {number} limit - Number of messages to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Object>} - Chat messages
 */
  static async getGroupChatMessages(eventId, chatType, getAuthHeaders, limit = 50, offset = 0) {
    try {
      console.log(`📡 [EventService] Getting messages for event ${eventId}, chat ${chatType}`);
      
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/groups/${chatType}/messages?limit=${limit}&offset=${offset}`,
        this.getHeaders(getAuthHeaders)
      );
      
      console.log(`✅ [EventService] Retrieved ${response.data.messages?.length || 0} messages`);
      
      if (response.data.demo) {
        console.log(`🎭 [EventService] Using demo messages for ${chatType} chat`);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ [EventService] Error fetching group chat messages:', error);
      throw error;
    }
  }

  /**
   * Send a message to a group chat
   * @param {number} eventId - The event ID
   * @param {string} chatType - The chat type ('interested' or 'joining')
   * @param {string} message - The message content
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - The sent message
   */
  static async sendGroupChatMessage(eventId, chatType, message, getAuthHeaders) {
    try {
      console.log(`📤 [EventService] Sending message to event ${eventId}, chat ${chatType}`);
      console.log(`💬 [EventService] Message preview: "${message.substring(0, 50)}${message.length > 50 ? '...' : ''}"`);
      
      const response = await axios.post(
        `${apiUrl}/events/${eventId}/groups/${chatType}/messages`,
        { message },
        this.getHeaders(getAuthHeaders)
      );
      
      console.log(`✅ [EventService] Message sent successfully`);
      
      if (response.data.demo) {
        console.log(`🎭 [EventService] Message sent in demo mode`);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ [EventService] Error sending group chat message:', error);
      throw error;
    }
  }

  /**
   * Get group information for an event
   * @param {number} eventId - The event ID
   * @param {string} chatType - The chat type ('interested' or 'joining')
   * @param {function} getAuthHeaders - Function to get authentication headers
   * @returns {Promise<Object>} - Group information
   */
  static async getGroupInfo(eventId, chatType, getAuthHeaders) {
    try {
      console.log(`ℹ️ [EventService] Getting group info for event ${eventId}, chat ${chatType}`);
      
      const response = await axios.get(
        `${apiUrl}/events/${eventId}/groups/${chatType}`,
        this.getHeaders(getAuthHeaders)
      );
      
      console.log(`✅ [EventService] Group info retrieved: ${response.data.groupName || 'Unknown'}`);
      
      return response.data;
    } catch (error) {
      console.error('❌ [EventService] Error fetching group info:', error);
      throw error;
    }
  }

  // ========== BATCH/SEARCH METHODS (Unchanged) ==========

  /**
   * Batch fetch events by IDs
   * @param {Array<number>} eventIds - Array of event IDs
   * @returns {Promise<Array>} - Array of events
   */
  static async getBatchEvents(eventIds) {
    try {
      const response = await axios.post(
        `${apiUrl}/events/batch`,
        { eventIds }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching batch events:', error);
      throw error;
    }
  }

  /**
   * Search events
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Object>} - Search results
   */
  static async searchEvents(searchParams) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/search`,
        { params: searchParams }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error searching events:', error);
      throw error;
    }
  }

  // ========== LEGACY/BACKWARD COMPATIBILITY METHODS ==========

  /**
   * Legacy method for backward compatibility with your existing EventActionButtons
   * @deprecated Use updateParticipationStatus instead
   */
  static async updateParticipantStatus(eventId, status, getAuthHeaders) {
    console.warn('updateParticipantStatus is deprecated. Use updateParticipationStatus instead.');
    return this.updateParticipationStatus(eventId, status, getAuthHeaders);
  }

  /**
   * Legacy method for backward compatibility - maps old API to new API
   * @deprecated Use getParticipationStatus instead
   */
  static async getParticipantStatus(eventId, getAuthHeaders) {
    console.warn('getParticipantStatus is deprecated. Use getParticipationStatus instead.');
    try {
      const response = await this.getParticipationStatus(eventId, getAuthHeaders);
      // Transform new format to old format for backward compatibility
      return {
        status: response.participationStatus
      };
    } catch (error) {
      console.error('Error in legacy getParticipantStatus:', error);
      throw error;
    }
  }

  /**
   * Legacy updateEvent method for backward compatibility
   * @deprecated This signature is deprecated, use the new options-based approach
   */
  static async updateEventLegacy(event, status, comments, getAuthHeaders, createGroup = true) {
    console.warn('updateEvent with boolean createGroup parameter is deprecated. Use options object instead.');
    return this.updateEvent(event, status, comments, getAuthHeaders, {
      createGroupForApproved: createGroup,
      statusOnly: false // Use full mode for backward compatibility
    });
  }

  // ========== NEW UTILITY METHODS FOR CREATOR VALIDATION ==========

  /**
   * 🔧 NEW: Validate creator information consistency across language versions
   * @param {number} masterEventId - The master event ID
   * @returns {Promise<Object>} - Validation results
   */
  static async validateCreatorConsistency(masterEventId) {
    try {
      const response = await axios.get(
        `${apiUrl}/events/${masterEventId}/language-versions`
      );
      
      const versions = response.data;
      if (versions.length <= 1) {
        return { consistent: true, message: 'Only one version exists' };
      }
      
      const firstVersion = versions[0];
      const inconsistencies = [];
      
      for (const version of versions) {
        if (version.event_creator_id !== firstVersion.event_creator_id) {
          inconsistencies.push({
            eventId: version.id,
            language: version.language,
            creatorId: version.event_creator_id,
            expected: firstVersion.event_creator_id
          });
        }
      }
      
      return {
        consistent: inconsistencies.length === 0,
        inconsistencies,
        totalVersions: versions.length
      };
    } catch (error) {
      console.error('Error validating creator consistency:', error);
      throw error;
    }
  }
}

export default EventService;

// Also export individual functions for backward compatibility with your existing code
export const getEvents = () => EventService.getEvents();
export const getEventById = (id) => EventService.getEventById(id);